import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { listAllPosts, deletePost, saveContent, triggerRebuild, formatApiError } from "../lib/api";
import { useContent } from "../context/ContentContext";

const badge = (status) =>
  status === "published"
    ? "bg-sage/15 text-sage-dark"
    : "bg-amber-100 text-amber-700";

// Undefined defaults to true so existing installs keep the blog visible.
const isBlogEnabled = (content) => content?.blogPage?.enabled !== false;

export const BlogManager = () => {
  const { content, setContent } = useContent();
  const enabled = isBlogEnabled(content);
  const [savingToggle, setSavingToggle] = useState(false);
  const [posts, setPosts] = useState(null);

  const load = async () => {
    try {
      const data = await listAllPosts();
      setPosts(data);
    } catch (e) {
      toast.error("Could not load posts");
      setPosts([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await deletePost(id);
      triggerRebuild("blog post deleted");
      toast.success("Post deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Delete failed");
    }
  };

  const toggleEnabled = async () => {
    const next = !enabled;
    setSavingToggle(true);
    try {
      const nextContent = {
        ...content,
        blogPage: { ...(content.blogPage || {}), enabled: next },
      };
      const saved = await saveContent(nextContent);
      setContent(saved);
      triggerRebuild("blog visibility toggled");
      toast.success(next ? "Blog is now visible on your site" : "Blog is now hidden from your site");
    } catch (err) {
      toast.error(formatApiError(err) || "Could not update visibility");
    } finally {
      setSavingToggle(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl text-ink">Blog</h1>
          <p className="text-sm text-ink-muted">Write and manage posts. Readers can browse and search — no comments or likes.</p>
        </div>
        <Link to="/admin/blog/new" data-testid="new-post-btn" className="inline-flex items-center gap-2 rounded-full bg-sage text-white px-5 py-2.5 text-sm font-semibold hover:bg-sage-dark transition-colors">
          <Plus className="h-4 w-4" /> New post
        </Link>
      </div>

      {/* Visibility toggle */}
      <div
        data-testid="blog-visibility-card"
        className={`mb-6 rounded-xl border p-5 md:p-6 flex items-start md:items-center justify-between gap-4 ${
          enabled ? "border-cream-2 bg-white" : "border-amber-200 bg-amber-50/60"
        }`}
      >
        <div className="flex items-start gap-3 min-w-0">
          <span
            className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
              enabled ? "bg-sage/15 text-sage-dark" : "bg-amber-100 text-amber-700"
            }`}
          >
            {enabled ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
          </span>
          <div className="min-w-0">
            <p className="font-serif text-lg text-ink">
              Blog is <span data-testid="blog-visibility-state" className={enabled ? "text-sage-dark" : "text-amber-700"}>{enabled ? "visible" : "hidden"}</span>
            </p>
            <p className="text-sm text-ink-muted mt-0.5">
              {enabled
                ? 'The "Blog" link appears in your site navigation and all published posts are readable.'
                : 'The "Blog" link is removed from navigation and all posts (including published ones) are hidden from visitors.'}
            </p>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="Toggle blog visibility"
          data-testid="blog-visibility-toggle"
          onClick={toggleEnabled}
          disabled={savingToggle}
          className={`relative shrink-0 inline-flex h-7 w-12 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-sage/50 disabled:opacity-60 ${
            enabled ? "bg-sage" : "bg-ink-muted/40"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform duration-300 ${
              enabled ? "translate-x-6" : "translate-x-1"
            }`}
          />
          {savingToggle && (
            <Loader2 className="absolute -right-6 h-4 w-4 animate-spin text-ink-muted" />
          )}
        </button>
      </div>

      {posts === null ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sage" /></div>
      ) : posts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-cream-2 p-12 text-center text-ink-muted">No posts yet. Create your first one.</div>
      ) : (
        <div className="space-y-3" data-testid="admin-post-list">
          {posts.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-4 rounded-xl border border-cream-2 bg-white p-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge(p.status)}`}>{p.status}</span>
                  <span className="text-xs text-ink-muted">{p.category}</span>
                </div>
                <p className="font-serif text-xl text-ink truncate mt-1">{p.title}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Link to={`/admin/blog/${p.id}`} data-testid={`edit-post-${p.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-cream-2 px-3 py-2 text-sm hover:bg-cream-2"><Pencil className="h-4 w-4" /> Edit</Link>
                <button onClick={() => remove(p.id)} data-testid={`delete-post-${p.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 text-red-600 px-3 py-2 text-sm hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogManager;
